import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@contexts/LanguageContext';
import { useNotification } from '@hooks/useNotification';
import { api } from '@services/api';
import FormPageLayout from '../FormPageLayout';
import './CreatePage.scss';

/**
 * Common create page component for all models
 *
 * @param {Object} props
 * @param {string} props.modelName - Name of the model (e.g., 'categories', 'resources', 'locations')
 * @param {React.ComponentType} props.FormComponent - Form component to render
 * @param {string} props.apiEndpoint - API endpoint for creating the resource
 * @param {string} props.backPath - Path to navigate back to (defaults to `/${modelName}`)
 * @param {string} props.successPath - Path to navigate to on success (defaults to `/${modelName}/{id}` - the view page)
 * @param {Object} props.formProps - Additional props to pass to the form component
 * @param {string} props.className - Additional CSS class
 * @param {React.ReactNode} props.headerActions - Additional header actions
 * @param {Function} props.transformData - Function to transform form data before API call
 * @param {Function} props.onSuccess - Custom success handler (receives created data)
 * @param {Function} props.onError - Custom error handler (receives error)
 */
const CreatePage = ({
    modelName,
    FormComponent,
    apiEndpoint,
    backPath,
    successPath,
    formProps = {},
    className = '',
    headerActions = null,
    transformData = (data) => data,
    onSuccess,
    onError
}) => {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const notify = useNotification();

    // Default paths
    const defaultBackPath = backPath || `/${modelName}`;
    const listPath = successPath || `/${modelName}`;

    const handleSubmit = async (formData) => {
        try {
            const transformedData = transformData(formData);
            const response = await api.post(apiEndpoint || `/${modelName}`, transformedData);

            notify.success(t(`${modelName}.createSuccess`));

            if (onSuccess) {
                onSuccess(response.data);
            } else {
                // Navigate to the view page of the created resource
                const resourceId = response.data.id;
                const viewPath = successPath || `/${modelName}/${resourceId}`;
                navigate(viewPath);
            }

            // Return the response so that form components can access it
            return response;
        } catch (err) {
            console.error(t(`${modelName}.createError`), err);
            notify.error(t(`${modelName}.createError`));

            if (onError) {
                onError(err);
            } else {
                throw err;
            }
        }
    };

    const handleCancel = () => {
        navigate(defaultBackPath);
    };

    // Generate title and subtitle
    const title = t(`${modelName}.create${modelName.charAt(0).toUpperCase() + modelName.slice(1, -1)}`);
    const subtitle = t(`${modelName}.createSubtitle`);

    return (
        <FormPageLayout
            title={title}
            subtitle={subtitle}
            onBack={handleCancel}
            className={`${modelName.slice(0, -1)}-create-page ${className}`}
            headerActions={headerActions}
        >
            <FormComponent
                data={null}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                isEditing={false}
                {...formProps}
            />
        </FormPageLayout>
    );
};

export default CreatePage;
